import { ReactNode, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import style from './UnarchiveListingModal.module.css'
import UnarchiveListingModalEventManager, { UnarchiveListingModalCustomEventType, UnarchiveListingModalEventData } from '../../../componentsNew/organisms/modal/UnarchiveListingModalEventManager';
import Modal from '../../organisms/modal/Modal';
import ColorEnum from '../../../enums/colorEnum';
import { ColorEnum as ColorEnumNew } from '../../../componentsNew/enums';
import ButtonNew from '../../../componentsNew/molecules/button/Button';
import updateListingsStatus from '../../../api/transactional-listing/updateListingsStatus';
import Text from '../../../componentsNew/atoms/typography/Text';
import { FontSizeEnum, FontWeightEnum } from '../../../componentsNew/enums';
import setEnrichmentToAutoForListings from '../../../api/transactional-listing/setEnrichmentToAutoForListings';
import setEnrichmentToManualForListings from '../../../api/transactional-listing/setEnrichmentToManualForListings';
import { ApprovedHomeseekers, findApprovedHomeseekersById, FindApprovedHomeseekersByIdResponse } from '../../../api/transactional-listing/findApprovedHomeseekersById';
import { CheckboxInput } from '../../../componentsNew/molecules/checkbox-input';
import { Badge } from '../../../componentsNew/molecules/badge';
import UserStarLineIcon from '../../../componentsNew/atoms/icons/UserStarLineIcon';
import { Tooltip } from '../../../componentsNew/atoms/tooltip';
import HighlightBlock from '../../../componentsNew/molecules/highlightBlock/HighlightBlock';
import { Link } from 'react-router-dom';
import { useRoutePath } from '../../../componentsNew/utils/useRoutePath';
import Notifications from '../../../componentsNew/organisms/notifications/Notifications';
import unassignTenantFromListing from '../../../api/transactional-listing/unassignTenantFromListing';
import ListingStatus from '../../../enums/listingStatus';
import UserUnfollowLineIcon from '../../../componentsNew/atoms/icons/UserUnfollowLineIcon';
import { classNames } from '../../../componentsNew/utils';
import SkeletonComponent from '../../SkeletonComponent';

const unarchiveListingModal = UnarchiveListingModalEventManager.create<UnarchiveListingModalEventData>('unarchive-modal');
type UnarchiveListingModalComponent = () => JSX.Element;

function UnarchiveListingModal() {
    const { t } = useTranslation();
    const routePath = useRoutePath();

    const [open, setOpen] = useState<boolean>(false);
    const [address, setAddress] = useState<string>(null);
    const [isBulkAction, setIsBulkAction] = useState<boolean>(false);
    const [transactionalListingIds, setTransactionalListingIds] = useState<string[]>(null);
    const [transactionalListingId, setTransactionalListingId] = useState<string>(null);
    const [autoEnrichmentEnabled, setAutoEnrichmentEnabled] = useState<boolean>(false);
    const [homeseekers, setHomeseekers] = useState<FindApprovedHomeseekersByIdResponse>(null);
    const [listingtenant, setListingTenant] = useState<ApprovedHomeseekers | null>(null);
    const [isUnassigning, setIsUnassigning] = useState<boolean>(false);
    const [isFindingHomeseekers, setIsFindingHomeseekers] = useState<boolean>(true);

    const setUnarchiveModalData = (e: UnarchiveListingModalCustomEventType) => {
        setOpen(e.detail.open);
        setAddress(e.detail.address);
        setIsBulkAction(e.detail.isBulkAction);
        setTransactionalListingIds(e.detail.transactionalListingIds);
        setTransactionalListingId(e.detail.transactionalListingId);
    }

    const fetchAllApprovedHomeseekers = async () => {
        try {
            const homeseekers = await findApprovedHomeseekersById(transactionalListingId);
            setHomeseekers(homeseekers);
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsFindingHomeseekers(false);
        }
    }

    useEffect(() => {
        if (transactionalListingId) {
            fetchAllApprovedHomeseekers();
        }
    }, [transactionalListingId]);

    useEffect(() => {
        if (homeseekers) {
            for (const homeseeker of homeseekers.approvedHomeseekers) {
                if (homeseeker.isTenant) {
                    setListingTenant(homeseeker);
                    break;
                }
            }
        }
    }, [homeseekers]);

    const handleSubmit = async () => {
        let listingsIds = [transactionalListingId];
        let notificationMessage: ReactNode = (
            <Trans
                i18nKey={'listings.unarchive.modal.succeeded'}
                values={{ address }}
                components={{ strong: <strong /> }}
            />
        );

        if (isBulkAction) {
            listingsIds = transactionalListingIds.slice();
            notificationMessage = `${transactionalListingIds.length} listings dearchiveerd`;
        }

        try {
            await updateListingsStatus({
                transactionalListingIds: listingsIds,
                status: ListingStatus.Open
            });

            if (autoEnrichmentEnabled) {
                setEnrichmentToAutoForListings({ transactionalListingIds: listingsIds });
            } else {
                setEnrichmentToManualForListings({ transactionalListingIds: listingsIds });
            }

            Notifications.add({
                type: 'success',
                message: notificationMessage
            });
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            handleModalClose();
        }
    }

    const handleCheckboxChange = (change: boolean) => {
        setAutoEnrichmentEnabled(change);
    }

    const openSettingsPage = () => {
        window.open(routePath('SettingsEmailTemplates'));
    }

    useEffect(() => {
        unarchiveListingModal.addListener(setUnarchiveModalData);

        return () => {
            unarchiveListingModal.removeListener(setUnarchiveModalData);
        }
    }, []);

    const handleModalClose = () => {
        setListingTenant(null);
        setAutoEnrichmentEnabled(false);

        unarchiveListingModal.close();
    }

    const handleUnassignTenant = async () => {
        try {
            await unassignTenantFromListing({
                homeseekerApplicationId: listingtenant.id,
                transactionalListingId: transactionalListingId
            });
            setIsUnassigning(true);

            Notifications.add({
                type: 'success',
                message: 'Huurder successvol verwijderd'
            });

            setTimeout(() => {
                setListingTenant(null);
            }, 250);
        } catch (e) {
            Notifications.add({
                type: 'failed',
                heading: t('api.request.failed')
            });

            setTimeout(() => {
                setListingTenant(null);
            }, 250);
        } finally {
            setTimeout(() => {
                setIsUnassigning(false);
                fetchAllApprovedHomeseekers();
            }, 250);
        }
    }

    const renderTenantBadge = () => {
        if (isBulkAction || !listingtenant) {
            return null;
        }

        return (
            <div className={classNames(style.listingTenant, isUnassigning && style.isUnassigning)}>
                <div className={style.tenantContainer}>
                    <Badge
                        before={<UserStarLineIcon />}
                        className={style.tenantBadge}
                        label={t('listings.archive.modal.tenantBadge')}
                    />
                    <Text className={style.tenantName}>
                        {`: ${listingtenant && listingtenant.name}`}
                    </Text>
                    {listingtenant &&
                        <div className={style.removeTenant} onClick={handleUnassignTenant}>
                            <Tooltip
                                anchor={<ButtonNew
                                    variant='tertiary-gray'
                                    iconBefore={<UserUnfollowLineIcon />}
                                    className={style.removeTenantButton}
                                >
                                </ButtonNew>}
                                size='sm'
                            >
                                <div className={style.tooltipContent}>
                                    {t('listings.unarchive.modal.deleteTenant')}
                                </div>
                            </Tooltip>
                        </div>
                    }
                </div>
            </div>
        )
    }

    const renderCheckboxLabel = () => {
        return (
            <div>
                <label htmlFor='autoEnrichment' className={style.checkboxLabel}>
                    <Text
                        size={FontSizeEnum.Md}
                        color={ColorEnumNew.Gray800}
                    >
                        {t('listings.unarchive.modal.checkbox')}
                    </Text>
                </label>
                <Text
                    color={ColorEnumNew.Gray500}
                    size={FontSizeEnum.Sm}
                >
                    {t('listings.unarchive.modal.checkbox.tooltip')}
                </Text>
            </div>
        );
    }

    if (!transactionalListingId && !isBulkAction) {
        return null;
    }

    return (
        <Modal
            isOpen={open}
            icon={'ri-inbox-line'}
            iconColor={ColorEnum.Primary600}
            iconBackgroundColor={ColorEnum.Primary100}
            onCloseButtonClick={handleModalClose}
            flowyColor={ColorEnum.Primary100}
            manager={unarchiveListingModal}
        >
            <div className={style.unarchiveContainer}>
                {isFindingHomeseekers ?
                    <>
                        <SkeletonComponent height="27px" width="100%"/>
                        <SkeletonComponent height="24px" width="100%"/>
                        <SkeletonComponent height="72px" width="100%"/>
                        <SkeletonComponent height="76px" width="100%"/>
                        <div className={style.buttonsContainer}>
                            <SkeletonComponent height="42px" width="100px"/>
                            <SkeletonComponent height="42px" width="150px"/>
                        </div>
                    </>
                    :
                    <>
                        <Text
                            size={FontSizeEnum.Lg}
                            weight={FontWeightEnum.Semibold}
                            color={ColorEnumNew.Gray800}
                        >
                            {!isBulkAction ?
                                t('listings.unarchive.modal.title', { address })
                                :
                                t('listings.unarchive.modal.bulkTitle', { count: transactionalListingIds.length })
                            }
                        </Text>
                        <Text
                            size={FontSizeEnum.Md}
                            color={!isBulkAction ? ColorEnumNew.Gray800 : ColorEnumNew.Gray500}
                        >
                            {!isBulkAction ?
                                t('listings.unarchive.modal.info')
                                :
                                t('listings.unarchive.modal.bulkInfo', { count: transactionalListingIds.length })}
                        </Text>
                        {renderTenantBadge()}
                        <div className={style.checkboxContainer} data-testid="autoEnrichment-checkbox">
                            <div className={style.checkbox}>
                                <CheckboxInput
                                    id="autoEnrichment"
                                    name="autoEnrichment"
                                    label={renderCheckboxLabel()}
                                    onChange={handleCheckboxChange}
                                    checked={autoEnrichmentEnabled}
                                />
                            </div>
                            <HighlightBlock
                                type='none'
                            >
                                {t('listings.unarchive.modal.highlightMessage')}
                                <Link
                                    to={''}
                                    onClick={openSettingsPage}
                                    className={style.emailLink}
                                >
                                    {t('listings.archive.modal.emailTemplates')}
                                </Link>
                            </HighlightBlock>
                            <div className={style.buttonsContainer}>
                                <ButtonNew
                                    variant='secondary-gray'
                                    className={style.modalCloseButton}
                                    onClick={handleModalClose}
                                >
                                    {t('listings.archive.modal.cancel')}
                                </ButtonNew>
                                <ButtonNew
                                    onClick={handleSubmit}
                                >
                                    {t('actions.move')}
                                </ButtonNew>
                            </div>
                        </div>
                    </>
                }
            </div>
        </Modal>
    )
}

Object.assign(UnarchiveListingModal, unarchiveListingModal);

export default (UnarchiveListingModal as UnarchiveListingModalComponent & UnarchiveListingModalEventManager<UnarchiveListingModalEventData>);
